import { useCallback, useEffect, useState } from 'react'
import {
  doc,
  getDoc,
  setDoc,
  deleteDoc,
  Timestamp,
  onSnapshot,
} from 'firebase/firestore'
import { deleteObject, ref } from 'firebase/storage'
import { db, storage } from '@/core/firebase'
import type { Media, MovementPattern, ExerciseEquipment } from '../types'

export type DraftType = 'exercise' | 'template'

// Form data that can be saved in a draft
export interface DraftFormData {
  name?: string
  description?: string
  movementPattern?: MovementPattern
  equipmentTags?: ExerciseEquipment[]
  targetMuscleGroups?: string[]
  howTo?: string
  defaultCues?: string
  defaultSets?: number
  defaultReps?: number
  defaultRestSec?: number
}

export interface Draft {
  trainerId: string
  type: DraftType
  media: Media[]
  formData?: DraftFormData
  updatedAt: Timestamp
}

interface UseDraftOptions {
  trainerId: string | undefined
  type: DraftType
  enabled?: boolean
}

const DRAFT_COLLECTION = 'drafts'

function getDraftDocPath(trainerId: string, type: DraftType) {
  return `${DRAFT_COLLECTION}/${trainerId}_${type}`
}

export function useDraft({ trainerId, type, enabled = true }: UseDraftOptions) {
  const [draft, setDraft] = useState<Draft | null>(null)
  const [loading, setLoading] = useState(true)

  // Subscribe to draft document
  useEffect(() => {
    if (!trainerId || !enabled) {
      setLoading(false)
      return
    }

    const docPath = getDraftDocPath(trainerId, type)
    const unsubscribe = onSnapshot(
      doc(db, docPath),
      (snapshot) => {
        if (snapshot.exists()) {
          setDraft(snapshot.data() as Draft)
        } else {
          setDraft(null)
        }
        setLoading(false)
      },
      (error) => {
        console.error('Error loading draft:', error)
        setLoading(false)
      }
    )

    return () => unsubscribe()
  }, [trainerId, type, enabled])

  // Save media to draft (preserves existing formData)
  const saveMedia = useCallback(
    async (media: Media[]) => {
      if (!trainerId) return

      const docPath = getDraftDocPath(trainerId, type)
      const draftData: Draft = {
        trainerId,
        type,
        media,
        formData: draft?.formData, // Preserve existing form data
        updatedAt: Timestamp.now(),
      }

      await setDoc(doc(db, docPath), draftData)
    },
    [trainerId, type, draft?.formData]
  )

  // Save form data to draft (preserves existing media)
  const saveFormData = useCallback(
    async (formData: DraftFormData) => {
      if (!trainerId) return

      const docPath = getDraftDocPath(trainerId, type)
      const draftData: Draft = {
        trainerId,
        type,
        media: draft?.media || [],
        formData,
        updatedAt: Timestamp.now(),
      }

      await setDoc(doc(db, docPath), draftData)
    },
    [trainerId, type, draft?.media]
  )

  // Add media to existing draft (with deduplication)
  const addMedia = useCallback(
    async (newMedia: Media | Media[]) => {
      if (!trainerId) return

      const mediaArray = Array.isArray(newMedia) ? newMedia : [newMedia]
      const currentMedia = draft?.media || []

      // Deduplicate: only add media items that don't already exist (by mediaId)
      const existingIds = new Set(currentMedia.map((m) => m.mediaId))
      const uniqueNewMedia = mediaArray.filter((m) => !existingIds.has(m.mediaId))

      if (uniqueNewMedia.length === 0) {
        // All media already exists, nothing to add
        return
      }

      await saveMedia([...currentMedia, ...uniqueNewMedia])
    },
    [trainerId, draft, saveMedia]
  )

  // Clear draft entirely (optionally delete all Storage files)
  const clearDraft = useCallback(
    async (deleteStorageFiles = true) => {
      if (!trainerId) return

      // Delete storage files if requested (including thumbnails)
      if (deleteStorageFiles && draft?.media) {
        const deletePromises: Promise<void>[] = []

        draft.media.forEach((m) => {
          // Delete main file
          if (m.storagePath) {
            deletePromises.push(
              deleteObject(ref(storage, m.storagePath)).catch((err) =>
                console.error('Failed to delete main file:', err)
              )
            )
          }
          // Delete thumbnail if exists
          if (m.thumbnailPath) {
            deletePromises.push(
              deleteObject(ref(storage, m.thumbnailPath)).catch((err) =>
                console.error('Failed to delete thumbnail:', err)
              )
            )
          }
        })

        await Promise.all(deletePromises)
      }

      // Delete the draft document only if it exists.
      const docPath = getDraftDocPath(trainerId, type)
      const draftRef = doc(db, docPath)
      const snapshot = await getDoc(draftRef)
      if (snapshot.exists()) {
        await deleteDoc(draftRef)
      }
      setDraft(null)
    },
    [trainerId, type, draft]
  )

  // Remove media from draft (and optionally delete from Storage)
  const removeMedia = useCallback(
    async (mediaId: string, deleteFromStorage = false) => {
      if (!trainerId || !draft) return

      const mediaToRemove = draft.media.find((m) => m.mediaId === mediaId)
      if (deleteFromStorage && mediaToRemove?.storagePath) {
        const deletePromises: Promise<void>[] = []

        // Delete main file
        deletePromises.push(
          deleteObject(ref(storage, mediaToRemove.storagePath)).catch((err) =>
            console.error('Failed to delete main file:', err)
          )
        )

        // Delete thumbnail if exists
        if (mediaToRemove.thumbnailPath) {
          deletePromises.push(
            deleteObject(ref(storage, mediaToRemove.thumbnailPath)).catch((err) =>
              console.error('Failed to delete thumbnail:', err)
            )
          )
        }

        await Promise.all(deletePromises)
      }

      const updatedMedia = draft.media.filter((m) => m.mediaId !== mediaId)
      if (updatedMedia.length === 0) {
        await clearDraft(false) // Don't delete storage again
      } else {
        await saveMedia(updatedMedia)
      }
    },
    [trainerId, draft, saveMedia, clearDraft]
  )

  // Consume draft (for when saving the actual entity - keeps media, clears draft)
  const consumeDraft = useCallback(async () => {
    if (!trainerId) return null

    const docPath = getDraftDocPath(trainerId, type)
    const draftRef = doc(db, docPath)
    const snapshot = await getDoc(draftRef)
    const currentDraft = snapshot.exists() ? (snapshot.data() as Draft) : draft

    if (snapshot.exists()) {
      await deleteDoc(draftRef)
    }
    setDraft(null)

    return currentDraft
  }, [trainerId, type, draft])

  // Check if draft has content (media or form data with meaningful values)
  const hasFormData = Boolean(
    draft?.formData &&
      (draft.formData.name?.trim() ||
        draft.formData.description?.trim() ||
        draft.formData.howTo?.trim() ||
        draft.formData.defaultCues?.trim() ||
        draft.formData.movementPattern ||
        (draft.formData.equipmentTags && draft.formData.equipmentTags.length > 0) ||
        (draft.formData.targetMuscleGroups && draft.formData.targetMuscleGroups.length > 0))
  )
  const hasDraft = Boolean((draft && draft.media.length > 0) || hasFormData)

  return {
    draft,
    loading,
    hasDraft,
    hasFormData,
    saveMedia,
    saveFormData,
    addMedia,
    removeMedia,
    clearDraft,
    consumeDraft,
  }
}

// Helper to load draft once (for initial form population)
export async function loadDraft(
  trainerId: string,
  type: DraftType
): Promise<Draft | null> {
  const docPath = getDraftDocPath(trainerId, type)
  const snapshot = await getDoc(doc(db, docPath))
  if (snapshot.exists()) {
    return snapshot.data() as Draft
  }
  return null
}
